<template>
  <!-- The birthday line on the first gift's send screen (recipient polish
       2026-11-03, DESIGN.md §19b, §20.1; prototype `bdLine`). Asked once, at
       the moment of sending, so the recipient's "You & <you>" rarely waits on
       a message. Quiet, never gold, never a step, and it never blocks the
       send: open, half-filled, saved or skipped, Send works. A half-filled
       date is simply not saved. Once saved, skipped or the gift is sent, it
       never shows again. -->
  <div v-if="state !== 'hidden'" class="sbl" data-testid="send-birthday-line">
    <div v-if="state === 'saved'" class="sbl-row">
      <span class="sbl-saved" role="status" data-testid="sbl-saved">✓ Your birthday is in. {{ who }} can see your stars together.</span>
    </div>
    <template v-else>
      <div class="sbl-row">
        <span class="sbl-t">✦ <b>Add your birthday</b> so {{ name }} can see your stars together.</span>
        <button v-if="state === 'offer'" type="button" class="sbl-add" data-testid="sbl-add" @click="state = 'open'">Add</button>
        <button type="button" class="sbl-x" data-testid="sbl-not-now" @click="skip">Not now</button>
      </div>
      <div v-if="state === 'open'" class="sbl-form">
        <BirthMoment mode="plain" :fields="['date', 'time', 'place']" :optional="['time', 'place']" :explain="false"
                     :closable="false" submit-label="Save my birthday" more-label="Add my birth time and place too"
                     more-sub="Optional. It opens the full picture of your stars together." :lock="LOCK_LINE" :need-date="true"
                     :saving="saving" :server-errors="errors" @save="save" />
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { store } from '@/store'
import BirthMoment from '@/components/widgets/BirthMoment.vue'
import { getBirthMoment, saveBirthMoment, navSeen } from '@/api-service'
import { LOCK_LINE, capFirst } from '@/utils/gift-steps'

const props = defineProps({
  // The recipient as the gift names them ("Sam").
  name: { type: String, default: '' },
  // The gift went out: the line is done for good.
  sent: { type: Boolean, default: false },
  // false when the server knows this is not their first gift.
  firstGift: { type: Boolean, default: null },
})

const SEEN_KEY = 'moment:send_birthday'
const LOCAL = 'ma_send_birthday_done'
const state = ref('hidden')       // hidden | offer | open | saved
const saving = ref(false)
const errors = ref(null)
const who = computed(() => capFirst(String(props.name || '').trim()) || 'They')

function doneHere () { try { return !!localStorage.getItem(LOCAL) } catch (e) { return false } }
function markDone () {
  try { localStorage.setItem(LOCAL, '1') } catch (e) { /* a nicety */ }
  navSeen(store.session_id, SEEN_KEY).catch(() => {})
}

onMounted(async () => {
  if (props.sent || props.firstGift === false || doneHere() || store.querent_birthdate) return
  let r = null
  try { r = await getBirthMoment() } catch (e) { return }
  if (r && r.moment && r.moment.birthdate) { markDone(); return }
  if (!props.sent) state.value = 'offer'
})

function skip () { state.value = 'hidden'; markDone() }

async function save (out) {
  saving.value = true; errors.value = null
  const body = { ...(out || {}) }
  delete body.form; delete body.legacy
  try {
    await saveBirthMoment(body)
    store.querent_birthdate = body.birthdate
    state.value = 'saved'
    markDone()
  } catch (e) {
    errors.value = (e && e.response && e.response.data && e.response.data.errors) || { _: 'That did not save. Try again.' }
  } finally { saving.value = false }
}

// Sent with the line still open (or untouched): it never comes back.
watch(() => props.sent, (on) => {
  if (!on) return
  if (state.value !== 'hidden' && state.value !== 'saved') markDone()
  if (state.value !== 'saved') state.value = 'hidden'
})
</script>

<style scoped>
.sbl { margin: 14px 0 4px; padding: 10px 12px; border-radius: 14px; border: 1px solid rgba(240, 217, 163, 0.22);
  background: rgba(20, 7, 31, 0.45); color: #e9dcff; font: 500 14.5px/1.45 Inter, system-ui, sans-serif; }
.sbl-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.sbl-t { flex: 1 1 220px; } .sbl-t b { color: #f0d9a3; }
.sbl-add, .sbl-x { min-height: 44px; min-width: 44px; padding: 0 14px; border-radius: 999px; cursor: pointer; font: 600 13.5px Inter, system-ui, sans-serif; }
.sbl-add { background: transparent; border: 1px solid rgba(240, 217, 163, 0.45); color: #f0d9a3; }
.sbl-x { background: none; border: 0; color: #b9abd3; text-decoration: underline; text-underline-offset: 3px; }
.sbl-add:focus-visible, .sbl-x:focus-visible { outline: 2px solid #f0d9a3; outline-offset: 2px; }
.sbl-saved { color: #ffe7b5; font-weight: 600; }
.sbl-form { margin-top: 8px; }
</style>
